/* oxlint-disable grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
import { Button } from "@grafit/design-system/components/molecules/button";
import { Input } from "@grafit/design-system/components/molecules/input";
import { Label } from "@grafit/design-system/components/molecules/label";
import { Trans, useTranslation } from "@grafit/i18n";
import { AlertCircle } from "lucide-react";
import * as React from "react";

const GHOST_BUTTON_VARIANT = { renderMode: "ghost", size: "s" } as const;

interface SignInFormProps {
  hasExpiredError: boolean;
  hasInvalidError: boolean;
  validatedEmail: string;
  updateValidatedEmail: (email: string) => Promise<void>;
  resetValidatedEmail: () => void;
  loading: boolean;
}

function isVinciEnergiesGroupEmail(email: string): boolean {
  return email.trim().toLowerCase().split("@")[1] === "vinci-energies.net";
}

export const SignInForm = React.memo<SignInFormProps>(
  ({ hasExpiredError, hasInvalidError, validatedEmail, updateValidatedEmail, resetValidatedEmail, loading }) => {
    const { t } = useTranslation();

    const emailId = React.useId();
    const emailRef = React.useRef<HTMLInputElement>(null);
    const [email, setEmail] = React.useState(validatedEmail);
    const showVinciGroupEmailWarning = isVinciEnergiesGroupEmail(email);

    const onValidateEmailClick = React.useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void updateValidatedEmail(email);
      },
      [updateValidatedEmail, email],
    );

    const onResetEmailClick = React.useCallback(() => {
      setEmail("");
      resetValidatedEmail();
      requestAnimationFrame(() => {
        emailRef.current?.focus();
      });
    }, [resetValidatedEmail]);

    return (
      <div className="space-y-4">
        {(hasInvalidError || hasExpiredError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 size-5 shrink-0 text-red-600" />
              <p className="flex-1 text-red-900 text-sm">
                <Trans
                  i18nKey={hasExpiredError ? "auth.sign-in.error.expired-link" : "auth.sign-in.error.invalid-link"}
                />
              </p>
            </div>
          </div>
        )}
        <form onSubmit={onValidateEmailClick} method="post">
          <div className="space-y-2">
            <Label htmlFor={emailId}>
              <Trans i18nKey="auth.sign-in.email.label" />
            </Label>
            <Input
              id={emailId}
              ref={emailRef}
              name="email"
              type="email"
              value={email}
              // oxlint-disable-next-line jsx-no-new-function-as-prop
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t((r) => r["auth.sign-in.email.placeholder"])}
              autoComplete="email"
              required
              disabled={!!validatedEmail}
            />
            {showVinciGroupEmailWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                  <p className="flex-1 text-amber-900 text-sm">
                    <Trans i18nKey="auth.sign-in.email.vinci-group-email-warning" />
                  </p>
                </div>
              </div>
            )}
            {validatedEmail ? (
              <div className="flex justify-end">
                <Button variant={GHOST_BUTTON_VARIANT} onClick={onResetEmailClick}>
                  <Trans i18nKey="auth.sign-in.email.change" />
                </Button>
              </div>
            ) : (
              <Button
                variant={{ kind: "primary", renderMode: "fill", size: "m" }}
                className="mt-4 w-full"
                type="submit"
                isPending={loading}
              >
                <Trans i18nKey="auth.sign-in.email.validate" />
              </Button>
            )}
          </div>
        </form>
      </div>
    );
  },
);
SignInForm.displayName = "SignInForm";
